import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  ApiException,
  Doc,
  GPanelPermissionsModel,
  PermissionsModel,
} from '../../../types';
import { useAnalytics } from '../../shared/analytics/create-events';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../state/zustand-store';
import {
  getDocumentAcls,
  getDocumentPermissions,
  updateDocumentAcls,
  UserKeys,
} from '../../api/users-api';
import { ModuleList } from '../../shared/analytics/module-list';
import { handleErrors } from '../../utils/errors';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import SharedDialog from '../../shared/shared-dialog';
import DriveFileVisibilityListItem from './drive-file-visibility-list-item';
import SharedAutocomplete from '../../shared/shared-autocomplete';
import DriveExpirationChip from './drive-search/drive-expiration-chip';
import ChangeLogDialog from '../../shared/change-log-dialog';
import SharedDrivesPermissionCheckDialog from '../user-management/user-details/drive/shared-drives/shared-drives-parent/shared-drives-permission-check-dialog';
import * as analytics from '../../shared/analytics/analytics.json';
import DriveFilePermissionListItem from './drive-file-permission-list-item';
import DriveFileVisibilityDialog from './drive-file-visibility-dialog';
import { emailRegex } from '../../utils/regular-expressions';
import axios from 'axios';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface Visibility {
  visibility: 'Private' | 'Domain' | 'Domain_With_Link' | 'Public_With_Link';
  role: string;
}

interface DriveFileEditPermissionsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDoc: Doc;
  setSelectedDoc: Dispatch<SetStateAction<Doc | null>>;
  primaryDomain: string;
  driveRoles: string[];
  changeLogRoles: string[];
  loggedInUser: string;
  isAllUsers?: boolean;
  onVisibilityUpdated?: (
    docId: string,
    foundUnder: string,
    updatedPermission: PermissionsModel | null
  ) => void;
}

export default function DriveFileEditPermissionsDialog({
  open,
  handleClose,
  primaryDomain,
  selectedDoc,
  setSelectedDoc,
  driveRoles,
  changeLogRoles,
  isAllUsers,
  loggedInUser,
  onVisibilityUpdated,
}: DriveFileEditPermissionsDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [isTryingToUpdatePermission, setIsTryingToUpdatePermission] =
    useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openPermissionCheckDialog, setOpenPermissionCheckDialog] =
    useState(false);
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState('');
  const [openVisibilty, setOpenVisibility] = useState(false);
  const { setSnackbar } = useStore();
  const [emailError, setEmailError] = useState('');

  const [failedToUpdatePermissions, setFailedToUpdatePermissions] =
    useState(false);

  const isShortcut =
    selectedDoc.mimeType === 'application/vnd.google-apps.shortcut';
  const targetFileId = isShortcut
    ? selectedDoc.shortcutDetails?.targetId
    : selectedDoc.id;
  const targetMimeType = isShortcut
    ? selectedDoc.shortcutDetails?.targetMimeType
    : selectedDoc.mimeType;

  const [visibility, setVisibility] = useState<Visibility>({
    visibility: 'Private',
    role: 'reader',
  });

  const isSharedDriveFile = Boolean(
    selectedDoc.driveId && selectedDoc.driveId !== ''
  );

  const ownerEmail = isSharedDriveFile
    ? loggedInUser
    : selectedDoc.ownerEmail
    ? selectedDoc.ownerEmail
    : selectedDoc.owners.length > 0
    ? selectedDoc.owners[0].emailAddress
    : '';

  const {
    data,
    isLoading,
    isFetching,
    isError: isAclError,
  } = useQuery({
    queryKey: [UserKeys.DriveAcls, ownerEmail, targetFileId, targetMimeType],
    queryFn: () => getDocumentAcls(ownerEmail, targetFileId!, targetMimeType!),
    enabled: ownerEmail !== '',
    staleTime: 0,
  });

  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    isFetching: isFetchingPermissions,
    isError: isPermissionsError,
  } = useQuery({
    queryKey: [UserKeys.DrivePermissions, ownerEmail, targetFileId],
    queryFn: () => getDocumentPermissions(ownerEmail, targetFileId!),
    staleTime: 0,
    enabled: ownerEmail !== '' && targetFileId !== '',
  });

  useEffect(() => {
    if (openPermissionCheckDialog) return;

    // if permission.type === 'anyone' or 'domain' w/ permission.role === 'writer', can update permissions
    if (
      permissions &&
      (permissions.type === 'anyone' || permissions.type === 'domain') &&
      permissions.role === 'writer'
    ) {
      return;
    }

    // if the permissions call errors, then the logged in user didn't have access
    const permError =
      isPermissionsError && !isLoadingPermissions && !isFetchingPermissions;

    // if the acl call errors, then the logged in user didn't have access
    const aclError = isAclError && !isLoading && !isFetching;

    // if the acl call didn't error, and there are no acls, then the logged in user didn't have access
    const aclWithNoPermissions =
      data && data.length === 0 && !isLoading && !isFetching;

    if (permError || aclError || aclWithNoPermissions) {
      setOpenPermissionCheckDialog(true);
    }
  }, [
    data,
    isAclError,
    isFetching,
    isFetchingPermissions,
    isLoading,
    isLoadingPermissions,
    isPermissionsError,
    openPermissionCheckDialog,
    permissions,
  ]);

  const { mutate, isPending: isSharing } = useMutation({
    mutationFn: (formData: GPanelPermissionsModel[]) =>
      updateDocumentAcls(
        targetFileId!,
        ownerEmail,
        targetMimeType!,
        formData,
        selectedDoc.title,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Document share permissions updated successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveAcls],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveFolders],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments],
      });

      setFailedToUpdatePermissions(false);
      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (
          errorResponse.message?.includes('sufficient permissions') ||
          errorResponse.message?.includes('required access')
        ) {
          setFailedToUpdatePermissions(true);
          setOpenPermissionCheckDialog(true);
        } else {
          handleErrors({
            error,
            snackbarMessage:
              'There was an issue updating document share permissions. Please refresh and try again.',
          });
        }
      }
    },
    onSettled: () => {
      const url = window.location.href.toLowerCase();
      const isDriveSearch = url.includes('search');
      const isSharedDrives = url.includes('shared');
      const isDriveExplorer = url.includes('explorer');

      if (isDriveSearch) {
        createEvent(analytics.drive.drive_search.update_drive_shares, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveSearch,
        });
      } else if (isDriveExplorer) {
        if (isAllUsers) {
          createEvent(analytics.drive.drive_explorer.update_drive_shares, {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.driveExplorer,
          });
        } else {
          createEvent(
            analytics.user_management.user_details.drive.update_drive_shares,
            {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.drive,
            }
          );
        }
      } else if (isSharedDrives) {
        createEvent(analytics.drive.shared_drives.update_drive_shares, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.sharedDrives,
        });
      }

      setOpenChangeLogDialog(false);
    },
  });

  const filteredData = (data?.filter((a) => a.userEmail) || []).sort((a, b) => {
    // First, sort owners to the top
    if (a.role === 'owner' && b.role !== 'owner') return -1;
    if (b.role === 'owner' && a.role !== 'owner') return 1;

    // Then, sort organizers second
    if (a.role === 'organizer' && b.role !== 'organizer') return -1;
    if (b.role === 'organizer' && a.role !== 'organizer') return 1;

    // Finally, sort by email
    return (a.userEmail || '').localeCompare(b.userEmail || '');
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm({
    values: {
      aclResources: filteredData,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'aclResources',
  });

  function mutateData() {
    // acls that have been added or changed
    const aclResources = getValues('aclResources') || [];
    const addedOrChangedData = aclResources.filter((acl) => acl.changeType);

    // acls that have been deleted
    const allEmails = data?.map((acl) => acl.userEmail) || [];
    const formEmails = aclResources.map((acl) => acl.userEmail);
    const deletedEmails = allEmails?.filter(
      (email) => !formEmails.includes(email)
    );
    const deletedData =
      data?.filter((acl) => deletedEmails?.includes(acl.userEmail)) || [];

    const modifiedDeletedData =
      deletedData?.map((acl) => {
        return {
          ...acl,
          changeType: 'delete' as 'delete' | 'add' | 'change',
          toBeDeleted: true,
        };
      }) || [];
    return [...addedOrChangedData, ...modifiedDeletedData];
  }

  const onSubmit: SubmitHandler<{
    aclResources: GPanelPermissionsModel[];
  }> = async () => {
    setIsTryingToUpdatePermission(true);

    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanShareDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      const formData = mutateData();
      mutate(formData);
    }
  };

  const watchedAcls = watch('aclResources');

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setSelectedDoc(null);
      }}
      title={`Share "${selectedDoc?.title}"`}
      actingAs={ownerEmail}
      dialogContent={
        <Stack>
          <Typography>General Access</Typography>
          {isLoadingPermissions || isFetchingPermissions ? (
            <Skeleton variant="rounded" height={60} />
          ) : (
            <DriveFileVisibilityListItem
              permissions={permissions}
              primaryDomain={primaryDomain}
              setVisibility={setVisibility}
            />
          )}
          <Button
            sx={{
              textTransform: 'none',
              color: 'inherit',
              textDecoration: 'underline',
              p: 0,
              ml: 'auto',
            }}
            onClick={() => setOpenVisibility(true)}
            disabled={
              !isAbleToSeeUIElement(
                driveRoles,
                DriveRoles.CanEditDocVisibility.value
              )
            }
          >
            Change
          </Button>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Stack flexDirection="row" gap={1} sx={{ mb: 1 }}>
              <Box sx={{ width: '100%' }}>
                <SharedAutocomplete
                  sources={['users', 'groups']}
                  autocompleteValue={inputValue}
                  setAutoCompleteValue={setInputValue}
                  freeSolo
                  onSelection={(value, type) => {
                    if (!value) return;
                    if (!emailRegex.test(value)) {
                      setEmailError(
                        'Invalid email address. Please enter a valid email.'
                      );
                      return;
                    }
                    if (fields.find((acl) => acl.userEmail === value)) {
                      setEmailError('This email is already added.');
                      return;
                    }
                    if (value === '') {
                      setEmailError('Email address cannot be empty.');
                      return;
                    }

                    append({
                      changeType: 'add',
                      userEmail: value,
                      role: 'reader',
                      type: (type as 'user' | 'group') || 'user',
                      withLink: false,
                      sendNotification: false,
                      transferOwnership: false,
                      additionalRoles: [],
                      isCommenter: false,
                      photoLink: '',
                      docId: targetFileId!,
                      mimeType: targetMimeType!,
                      isPrimaryOwner: false,
                      toBeDeleted: false,
                      permExpirationTime: 0,
                      permId: '',
                      docOwner: selectedDoc.ownerEmail,
                      docTitle: selectedDoc.title,
                      allowFileDiscovery: false,
                      displayName: '',
                      permissionDetails: [],
                    });

                    setInputValue('');
                    setEmailError('');
                  }}
                />
              </Box>
            </Stack>
            {emailError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {emailError}
              </Typography>
            )}

            <Typography>People with access</Typography>
            {isLoading || isFetching ? (
              <Stack gap={1}>
                <Skeleton variant="rounded" height={60} />
              </Stack>
            ) : (
              <List sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {fields.map((acl, index) => {
                  const watchedAcl = watch(`aclResources.${index}`);
                  return (
                    <Stack key={acl.id}>
                      <DriveFilePermissionListItem
                        isSharedDriveFile={isSharedDriveFile}
                        watchedAcls={watchedAcls}
                        index={index}
                        control={control}
                        setValue={setValue}
                        filteredData={filteredData}
                        remove={remove}
                        watchedAcl={watchedAcl}
                        acl={acl}
                        selectedDoc={selectedDoc}
                      />
                      <DriveExpirationChip
                        control={control}
                        index={index}
                        watchedAcl={watchedAcl}
                        acl={acl}
                        setValue={setValue}
                      />
                    </Stack>
                  );
                })}
              </List>
            )}
          </Box>
          {openVisibilty && (
            <DriveFileVisibilityDialog
              open={openVisibilty}
              handleClose={() => {
                setOpenVisibility(false);
              }}
              visibility={visibility}
              selectedDoc={selectedDoc}
              primaryDomain={primaryDomain}
              permission={permissions}
              changeLogRoles={changeLogRoles}
              ownerEmail={ownerEmail}
              onVisibilityUpdated={onVisibilityUpdated}
            />
          )}
          {openChangeLogDialog && (
            <ChangeLogDialog
              open={openChangeLogDialog}
              handleClose={() => setOpenChangeLogDialog(false)}
              changeLogMessage={changeLogMessage}
              setChangeLogMessage={setChangeLogMessage}
              mutate={() => {
                const formData = mutateData();
                mutate(formData);
              }}
              mutateLoading={isSharing}
            />
          )}
          {openPermissionCheckDialog && (
            <SharedDrivesPermissionCheckDialog
              folderOrDriveId={selectedDoc.driveId || ''}
              open={openPermissionCheckDialog}
              handleClose={() => {
                setOpenPermissionCheckDialog(false);
                handleClose();
              }}
              minimumRequiredRole={
                failedToUpdatePermissions ? 'fileOrganizer' : 'writer'
              }
              actionAfterPermissionGranted={() => {
                if (isTryingToUpdatePermission) {
                  if (
                    isAbleToSeeUIElement(
                      changeLogRoles,
                      DriveRoles.CanShareDocs.value
                    )
                  ) {
                    setOpenChangeLogDialog(true);
                  } else {
                    const formData = mutateData();
                    mutate(formData);
                  }
                } else {
                  queryClient.invalidateQueries({
                    queryKey: [UserKeys.DrivePermissions],
                  });
                  queryClient.invalidateQueries({
                    queryKey: [UserKeys.DriveAcls],
                  });
                }

                setIsTryingToUpdatePermission(false);
                setOpenPermissionCheckDialog(false);
              }}
              nameOfSharedDrive={null}
            />
          )}
        </Stack>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        loading: isSharing,
        disabled:
          !isDirty ||
          isSharing ||
          !isAbleToSeeUIElement(driveRoles, DriveRoles.CanShareDocs.value),
      }}
    />
  );
}
